import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { studentSchema } from '../../utils/validation';
import { StudentFormData, SchoolClass, FeeStructure } from '../../types';
import { schoolDataApi, feeStructureApi } from '../../services/api';
import { sortClasses } from '../../utils/classUtils';
import { formatCurrency } from '../../utils/helpers';
import { Upload, X, User, Calendar, MapPin, Phone, Mail, Users, GraduationCap, IndianRupee } from 'lucide-react';

interface StudentFormProps {
  onSubmit: (data: StudentFormData) => void;
  loading?: boolean;
  initialData?: Partial<StudentFormData>;
  onCancel?: () => void;
}

const StudentForm: React.FC<StudentFormProps> = ({
  onSubmit,
  loading = false,
  initialData,
  onCancel
}) => {
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [feeStructures, setFeeStructures] = useState<FeeStructure[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<StudentFormData>({
    defaultValues: initialData
  });

  useEffect(() => {
    schoolDataApi.classes.getAll().then(data => setClasses(sortClasses(data))).catch(() => setClasses([]));
    feeStructureApi.getAll().then(setFeeStructures).catch(() => setFeeStructures([]));
  }, []);

  useEffect(() => {
    // The class/section <select>s have no matching <option> until `classes`
    // finishes loading, so react-hook-form's one-time `defaultValues` can't
    // select them at mount. This effect re-applies the initial values after
    // `classes` changes, i.e. after React has already rendered the matching
    // <option> elements — so the DOM select actually shows them as selected,
    // not just react-hook-form's internal (uncontrolled) form state.
    if (classes.length === 0) return;
    if (initialData?.class) setValue('class', initialData.class);
    if (initialData?.section) setValue('section', initialData.section);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes]);

  const selectedClassName = watch('class');
  const availableSections = classes.find(c => c.name === selectedClassName)?.sections || [];
  const monthlyFeeStructure = feeStructures.find(
    fs => fs.className === selectedClassName && fs.type === 'monthly' && fs.isActive
  );

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // Use toast instead of alert for better UX
        // toast.error('Photo size should be less than 5MB');
        return;
      }

      if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
        // Use toast instead of alert for better UX
        // toast.error('Please select a valid image file (JPEG, JPG, PNG)');
        return;
      }

      setSelectedPhoto(file);

      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview('');
    setValue('photo', undefined);
  };

  const onFormSubmit = (data: StudentFormData) => {
    const formData: StudentFormData = {
      ...data,
      photo: selectedPhoto || undefined
    };
    onSubmit(formData);
  };

  return (
    <div className="bg-[#211c15] rounded-xl border border-[#3a3122] p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-[#f3ede2] mb-2">
          {initialData ? 'Edit Student' : 'Add New Student'}
        </h2>
        <p className="text-[#b9ad9b]">
          {initialData ? 'Update student information' : 'Enter student details to register'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {/* Photo Upload */}
        <div className="flex justify-center">
          <div className="relative">
            {photoPreview ? (
              <div className="relative">
                <img
                  src={photoPreview}
                  alt="Student"
                  className="w-32 h-32 rounded-full object-cover border-4 border-[#3a3122]"
                />
                <button
                  type="button"
                  onClick={removePhoto}
                  className="absolute -top-2 -right-2 w-8 h-8 bg-[#f38b83] text-white rounded-full flex items-center justify-center hover:bg-[#f38b83] transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="w-32 h-32 rounded-full border-2 border-dashed border-[#4a4030] flex items-center justify-center bg-[#211c15]">
                <User className="w-12 h-12 text-[#8a7f6e]" />
              </div>
            )}
            <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#e2916d] text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-[#f0a67d] transition-colors">
              <Upload className="w-4 h-4" />
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="bg-[#17140f] text-[#f3ede2] placeholder-[#8a7f6e] hidden"
              />
            </label>
          </div>
        </div>

        {/* Personal Information */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4 flex items-center">
            <User className="w-5 h-5 mr-2 text-[#e2916d]" />
            Personal Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Student Name *
              </label>
              <input
                type="text"
                {...register('name')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter student name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Date of Birth *
              </label>
              <input
                type="date"
                {...register('dateOfBirth')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              />
              {errors.dateOfBirth && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.dateOfBirth.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Gender *
              </label>
              <select
                {...register('gender')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
              {errors.gender && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.gender.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Permanent Address *
              </label>
              <textarea
                {...register('permanentAddress')}
                rows={3}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter permanent address"
              />
              {errors.permanentAddress && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.permanentAddress.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Current Address *
              </label>
              <textarea
                {...register('currentAddress')}
                rows={3}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter current address"
              />
              {errors.currentAddress && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.currentAddress.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Academic Information */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4 flex items-center">
            <GraduationCap className="w-5 h-5 mr-2 text-[#e2916d]" />
            Academic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Class *
              </label>
              <select
                {...register('class')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
              >
                <option value="">Select Class</option>
                {classes.map((cls) => (
                  <option key={cls.id} value={cls.name}>{cls.name}</option>
                ))}
              </select>
              {errors.class && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.class.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Section
              </label>
              <select
                {...register('section')}
                disabled={!selectedClassName}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select Section</option>
                {availableSections.map((section) => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </select>
            </div>
          </div>

          {selectedClassName && (
            <div className="mt-4 flex items-center space-x-2 text-sm">
              <IndianRupee className="w-4 h-4 text-[#e2916d] flex-shrink-0" />
              {monthlyFeeStructure ? (
                <p className="text-[#b9ad9b]">
                  Monthly fee for {selectedClassName}:{' '}
                  <span className="text-[#f3ede2] font-medium">{formatCurrency(monthlyFeeStructure.totalAmount)}</span>
                  {' '}— will be applied automatically when saved.
                </p>
              ) : (
                <p className="text-[#8a7f6e]">No monthly fee structure configured for {selectedClassName} yet.</p>
              )}
            </div>
          )}
        </div>

        {/* Parent Information */}
        <div>
          <h3 className="text-lg font-medium text-[#f3ede2] mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-[#e2916d]" />
            Parent Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Parent Name *
              </label>
              <input
                type="text"
                {...register('parentName')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter parent name"
              />
              {errors.parentName && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.parentName.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Parent Contact *
              </label>
              <input
                type="tel"
                {...register('parentContact')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="10-digit mobile number"
              />
              {errors.parentContact && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.parentContact.message}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-[#b9ad9b] mb-2">
                Parent Email *
              </label>
              <input
                type="email"
                {...register('parentEmail')}
                className="w-full px-3 py-2 border border-[#3a3122] rounded-lg focus:ring-2 focus:ring-[#e2916d] focus:border-transparent bg-[#17140f] text-[#f3ede2]"
                placeholder="Enter parent email address"
              />
              {errors.parentEmail && (
                <p className="mt-1 text-sm text-[#f38b83]">{errors.parentEmail.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6 border-t border-[#3a3122]">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-[#3a3122] text-[#b9ad9b] rounded-lg hover:bg-[#211c15] transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-[#e2916d] hover:bg-[#f0a67d] disabled:bg-[#8a7f6e] text-white rounded-lg transition-colors flex items-center space-x-2"
          >
            {loading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            <span>{initialData ? 'Update Student' : 'Add Student'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default StudentForm;